import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { updatePasswordSchema, UpdatePasswordFormValues } from '@/features/auth/schemas'
import { useAuth } from '@/context/auth-context'

function UpdatePasswordForm() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)

  // Use Auth Context
  const { user, signIn, updatePassword } = useAuth()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdatePasswordFormValues>({
    resolver: zodResolver(updatePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  })

  const onSubmit = async (data: UpdatePasswordFormValues) => {
    try {
      setIsLoading(true)
      setError(null)
      setIsSuccess(false)

      // First verify the current password
      const { error: signInError } = await signIn({
        email: user?.email || '',
        password: data.currentPassword,
      })

      if (signInError) {
        setError('Current password is incorrect')
        return
      }

      // Then update to the new password
      const { error: updateError } = await updatePassword(data.newPassword)

      if (updateError) {
        setError(updateError)
        return
      }

      setIsSuccess(true)
      reset() // Clear the form

      // Hide success message after 3 seconds
      setTimeout(() => setIsSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full max-w-md space-y-6">
      <div>
        <h2 className="text-xl font-bold text-text">Change Password</h2>
        <p className="text-text/70 mt-1">Update your password to keep your account secure</p>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-500 text-sm">
          {error}
        </div>
      )}

      {isSuccess && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-md text-green-500 text-sm">
          Password updated successfully!
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="currentPassword" className="text-sm font-medium text-text">
            Current Password
          </label>
          <input
            id="currentPassword"
            type="password"
            placeholder="••••••••"
            className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            {...register('currentPassword')}
          />
          {errors.currentPassword && (
            <p className="text-red-500 text-xs mt-1">{errors.currentPassword.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="newPassword" className="text-sm font-medium text-text">
            New Password
          </label>
          <input
            id="newPassword"
            type="password"
            placeholder="••••••••"
            className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            {...register('newPassword')}
          />
          {errors.newPassword && (
            <p className="text-red-500 text-xs mt-1">{errors.newPassword.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="confirmNewPassword" className="text-sm font-medium text-text">
            Confirm New Password
          </label>
          <input
            id="confirmNewPassword"
            type="password"
            placeholder="••••••••"
            className="w-full px-3 py-2 border border-gray-700 rounded-md bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            {...register('confirmNewPassword')}
          />
          {errors.confirmNewPassword && (
            <p className="text-red-500 text-xs mt-1">{errors.confirmNewPassword.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Updating...' : 'Update Password'}
        </button>
      </form>
    </div>
  )
}

export { UpdatePasswordForm }
